import React from 'react';
import { StatsCard } from '../../common/Card';

const ANALYTICS_ICON_BG = {
  blue: 'bg-info-light',
  green: 'bg-success-light',
  purple: 'bg-primary/10',
  orange: 'bg-warning-light',
};

function KpiTile({ label, value, subtitle, icon, iconBg }) {
  return (
    <div className="flex min-w-[7.5rem] flex-1 flex-col items-center justify-center px-3 py-3 text-center sm:min-w-0">
      {icon ? (
        <div className={`mb-1.5 flex h-8 w-8 items-center justify-center rounded-lg border border-border ${iconBg}`}>
          <span className="text-base leading-none">{icon}</span>
        </div>
      ) : null}
      <p className="text-lg font-bold tabular-nums leading-tight text-text-primary">{value}</p>
      <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-text-muted">{label}</p>
      {subtitle ? <p className="mt-0.5 line-clamp-2 text-[10px] leading-tight text-text-secondary">{subtitle}</p> : null}
    </div>
  );
}

/** Label-first mobile tile — 2-col grid, optional full-width rows. */
function CompactStatTile({ label, mobileLabel, value, subtitle, fullWidth, layout = 'stacked' }) {
  const displayLabel = mobileLabel ?? label;

  if (layout === 'inline') {
    return (
      <div className="col-span-2 flex items-center justify-between gap-3 rounded-lg border border-border-light bg-surface-subtle px-3 py-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">{displayLabel}</p>
        <p className="shrink-0 text-sm font-bold tabular-nums leading-tight text-text-primary">{value}</p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border border-border-light bg-surface-subtle px-3 py-2.5 ${
        fullWidth ? 'col-span-2' : ''
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">{displayLabel}</p>
      <p className="mt-0.5 text-sm font-bold tabular-nums leading-tight text-text-primary">{value}</p>
      {subtitle ? (
        <p className="mt-1 line-clamp-2 text-xs leading-snug text-text-secondary">{subtitle}</p>
      ) : null}
    </div>
  );
}

/**
 * KPI metrics — horizontal scroll strip on mobile, StatsCard grid on lg+.
 * Pass `dense` for player analysis: 2-column mobile grid with a full-width last-session row.
 */
export default function ClubAnalysisKpiGrid({ items = [], dense = false }) {
  return (
    <>
      {dense ? (
        <div className="mb-4 grid grid-cols-2 gap-2 lg:hidden">
          {items.map((item) => (
            <CompactStatTile
              key={item.key}
              label={item.label}
              mobileLabel={item.mobileLabel}
              value={item.value}
              subtitle={item.subtitle}
              fullWidth={item.fullWidth}
              layout={item.layout}
            />
          ))}
        </div>
      ) : (
        <div className="mb-6 flex divide-x divide-border-light overflow-x-auto rounded-lg border border-border-medium bg-surface shadow-brand-sm scrollbar-hide lg:hidden">
          {items.map((item) => (
            <KpiTile key={item.key} {...item} />
          ))}
        </div>
      )}

      <div className={`hidden gap-4 lg:grid lg:grid-cols-4 lg:gap-6 ${dense ? 'mb-4' : 'mb-6'}`}>
        {items.map((item) => (
          <StatsCard
            key={item.key}
            title={item.label}
            value={item.value}
            subtitle={item.subtitle}
            icon={item.icon}
            iconBg={item.iconBg}
          />
        ))}
      </div>
    </>
  );
}

export { ANALYTICS_ICON_BG };
